import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { IntercomService } from '@principle-theorem/ng-intercom';
import {
  type IBalance,
  type IInvoice,
  type IInvoiceItemGroupWithProvider,
  type IInvoiceWithProviderDetails,
  ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshot,
  Timestamp,
  type WithRef,
  toMoment,
  shareReplayCold,
} from '@principle-theorem/shared';
import { sanitiseCSRF } from '@principle-theorem/temporary-tokens';
import html2pdf from 'html2pdf.js';
import { isNull, kebabCase, toArray } from 'lodash';
import { BehaviorSubject, combineLatest, type Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PatientViewInvoiceService } from './patient-view-invoice.service';

@Component({
  selector: 'pr-patient-view-invoice',
  templateUrl: './patient-view-invoice.component.html',
  styleUrls: ['./patient-view-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientViewInvoiceComponent implements OnInit, OnDestroy {
  @ViewChild('invoiceDisplay') invoiceDisplay: ElementRef<HTMLElement>;
  tokenUid$: Observable<string | undefined>;
  data$: Observable<IInvoiceWithProviderDetails | undefined>;
  invoice$: Observable<WithRef<IInvoice> | undefined>;
  balance$: Observable<IBalance | undefined>;
  transactions$: Observable<WithRef<ITransaction<unknown>>[] | undefined>;
  groups$: Observable<IInvoiceItemGroupWithProvider[] | undefined>;
  treatmentDate$: Observable<Timestamp | undefined>;
  isLoading$ = new BehaviorSubject<boolean>(true);
  isAboveSmall$: Observable<boolean>;

  constructor(
    private _route: ActivatedRoute,
    private _patientInvoiceService: PatientViewInvoiceService,
    private _intercom: IntercomService,
    breakpointObserver: BreakpointObserver
  ) {
    this.isAboveSmall$ = breakpointObserver
      .observe(['(min-width: 960px)'])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
    this.tokenUid$ = combineLatest([
      this._route.queryParamMap.pipe(
        map((queryParams) => queryParams.get('token')),
        map((token) => (!isNull(token) ? token : undefined))
      ),
      this._route.paramMap.pipe(
        map((params) => params.get('token')),
        map((token) => (!isNull(token) ? token : undefined))
      ),
    ]).pipe(
      map(([queryToken, routeToken]) => queryToken ?? routeToken),
      map((token) => token && sanitiseCSRF(token))
    );

    this.data$ = this.tokenUid$.pipe(
      switchMap((tokenUid) =>
        tokenUid
          ? this._patientInvoiceService.resolveInvoice(tokenUid)
          : of(undefined)
      ),
      tap(() => this.isLoading$.next(false))
    );

    this.invoice$ = this.data$.pipe(map((data) => data?.invoice));
    this.groups$ = this.data$.pipe(map((data) => data?.groups));
    this.balance$ = this.data$.pipe(map((data) => data?.balance));
    this.treatmentDate$ = this.data$.pipe(map((data) => data?.treatmentDate));
    this.transactions$ = this.data$.pipe(map((data) => data?.transactions));
  }

  printInvoice(): void {
    window.print();
  }

  async downloadInvoice(): Promise<void> {
    const invoice = await snapshot(this.invoice$);
    if (!invoice) {
      return;
    }

    const content = this.invoiceDisplay.nativeElement;

    toArray(content.getElementsByClassName('tooltip'))
      .map((element) => element as HTMLElement)
      .map((tooltip) => (tooltip.style.display = 'none'));

    const filename = `${kebabCase(invoice.practice.name)}-invoice-${toMoment(
      invoice.createdAt
    ).format('DD-MM-YYYY')}.pdf`;

    await html2pdf()
      .set({
        margin: 4,
        pagebreak: { mode: 'avoid-all' },
        filename,
      })
      .from(content)
      .save();
  }

  ngOnInit(): void {
    this._intercom.hideIcon();
  }

  ngOnDestroy(): void {
    this._intercom.showIcon();
  }
}
